import React from 'react';
import { Box, Stack, Typography, Button, Grid, Card } from '@mui/material';
import { gradientTextSx } from '../../../utils/textStyles';
import { isNeonTheme } from '../../../utils/themeTraits';

const hexToRgb = (hex) => {
  if (!hex) return "255, 255, 255";
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
    : "255, 255, 255";
};

// The booking page's day column on both dark themes. The neon one (herevr) glows and pulses the
// chosen slot; the other one (2fadedbarbershop) keeps the same frame in hairlines and fills the
// chosen slot with its action colour instead.
const FuturisticDayColumn = ({ dailySchedule, selectedAppointment, onAppointmentClick, displayConfig }) => {
  const neon = isNeonTheme(displayConfig);
  const glowColor = displayConfig?.primaryColor || "#108bdd";
  const glowRgb = hexToRgb(glowColor);
  const headerGlowColor = displayConfig?.buttonColor || "#108bdd";
  const headerGlowRgb = hexToRgb(headerGlowColor);
  const date = dailySchedule.date;

  return (
    <>
      {neon && <style>{`
        @keyframes pulseGlow {
          0%, 100% { box-shadow: 0 0 12px rgba(${glowRgb}, 0.8), 0 0 24px rgba(${glowRgb}, 0.4); }
          50% { box-shadow: 0 0 20px rgba(${glowRgb}, 1), 0 0 40px rgba(${glowRgb}, 0.6); }
        }
      `}</style>}

      <Grid key={date} item xs={4} sm={3} md={3} lg={2} data-testid="booking-day" data-date={date}>
        <Card sx={{
          background: displayConfig?.sectionBackground,
          color: displayConfig?.color,
          border: `1px solid rgba(${glowRgb}, ${neon ? 0.4 : 0.3})`,
          boxShadow: neon
            ? `0 0 10px rgba(${glowRgb}, 0.15), inset 0 0 10px rgba(${glowRgb}, 0.03)`
            : "0 18px 40px -24px rgba(0, 0, 0, 0.9)",
        }}>
          {/* Column header */}
          <Stack
            direction={{ xs: "column", xl: "row" }}
            justifyContent="space-around"
            alignItems="center"
            p={1}
            sx={{
              bgcolor: "black",
              borderBottom: `1px solid ${headerGlowColor}`,
              boxShadow: neon ? `0 2px 12px rgba(${headerGlowRgb}, 0.5)` : "none",
            }}
          >
            <Typography variant="h6" textTransform="capitalize" sx={gradientTextSx(displayConfig, displayConfig?.scheduleItemHeaderColor)} noWrap>
              {dailySchedule.weekday}
            </Typography>
            <Typography variant="body1" color="grey.200" noWrap fontSize={{ xs: 12, sm: 14, md: 16 }}>
              {dailySchedule.formattedDate}
            </Typography>
          </Stack>

          {/* Time slots */}
          <Box sx={{ height: "50vh", overflowY: "auto" }}>
            <Stack p={1} spacing={1}>
              {dailySchedule.timeSlots.map(timeSlot => {
                const isSelected = selectedAppointment?.date === date && selectedAppointment?.time === timeSlot?.startTime;
                return (
                  <Button
                    key={`${date}-${timeSlot?.startTime}`}
                    variant={isSelected ? "contained" : "outlined"}
                    onClick={() => onAppointmentClick(date, dailySchedule.weekday, timeSlot?.startTime)}
                    sx={{
                      transition: "all 0.2s ease",
                      border: `1px solid rgba(${glowRgb}, 0.3)`,
                      color: glowColor,
                      "&:hover": {
                        background: `rgba(${glowRgb}, 0.1)`,
                        border: `1px solid ${glowColor}`,
                        boxShadow: neon ? `0 0 8px rgba(${glowRgb}, 0.6)` : "none",
                        color: glowColor,
                      },
                      ...(isSelected && neon && {
                        background: `rgba(${glowRgb}, 0.15)`,
                        border: `1px solid ${glowColor}`,
                        color: glowColor,
                        fontWeight: "bold",
                        animation: "pulseGlow 1.5s ease-in-out infinite",
                      }),
                      ...(isSelected && !neon && {
                        background: glowColor,
                        border: `1px solid ${glowColor}`,
                        color: displayConfig?.buttonTextColor || "#000",
                        fontWeight: "bold",
                        "&:hover": { background: glowColor, color: displayConfig?.buttonTextColor || "#000" },
                      }),
                    }}
                  >
                    {timeSlot?.startTime}
                  </Button>
                );
              })}
            </Stack>
          </Box>
        </Card>
      </Grid>
    </>
  );
};

export default FuturisticDayColumn;